import React, { useState, useEffect } from 'react';
import { 
  Crown, Trophy, Star, DollarSign, TrendingUp, 
  Calendar, Users, Target, BarChart3, Award,
  ChevronRight, Zap, Medal, Activity
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const HighestPaidPlayerMemorial = ({ draftData, loading, error }) => {
  const [playerData, setPlayerData] = useState(null);
  const [yearlyInvestments, setYearlyInvestments] = useState([]);
  const [managerInvestments, setManagerInvestments] = useState([]);
  const [allPlayerDots, setAllPlayerDots] = useState([]);
  const [hoveredDot, setHoveredDot] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });

  // Utility function to get player headshot with fallbacks
  const getPlayerHeadshot = (playerName) => {
    if (!playerName) return null;
    
    // Clean player name for checking
    const cleanName = playerName.trim();
    
    // Check for specific players with local images
    if (cleanName === "Julio Jones" || cleanName === "Jones" || cleanName === "Julio") {
      return ['/julio.png'];
    }
    
    // Clean player name for URL formatting for other players
    const nameForUrl = cleanName.toLowerCase().replace(/[^a-z\s]/g, '').replace(/\s+/g, '-');
    
    // Multiple fallback sources for other players
    const sources = [
      // ESPN (most reliable for current players)
      `https://a.espncdn.com/i/headshots/nfl/players/full/${nameForUrl}.png`,
      // NFL.com alternative
      `https://static.www.nfl.com/image/private/t_headshot_desktop/nfl/players/${nameForUrl}`,
      // Sports Reference (good for historical players)
      `https://www.pro-football-reference.com/req/202301051/images/headshots/${nameForUrl}_2023.jpg`,
      // Generic fallback
      `https://via.placeholder.com/120x120/e5e7eb/6b7280?text=${playerName.split(' ').map(n => n[0]).join('')}`
    ];
    
    return sources;
  };

  // Component to handle image loading with fallbacks - Enhanced stability
  const PlayerHeadshot = React.memo(({ playerName, className = "", size = 120 }) => {
    const [imageState, setImageState] = useState({
      currentSrcIndex: 0,
      isLoading: true,
      hasError: false,
      loadedSrc: null,
      hasSuccessfullyLoaded: false
    });
    
    const sources = React.useMemo(() => getPlayerHeadshot(playerName), [playerName]);
    const stablePlayerName = React.useMemo(() => playerName?.trim(), [playerName]);
    
    // Only reset when playerName actually changes
    useEffect(() => {
      setImageState({
        currentSrcIndex: 0,
        isLoading: true,
        hasError: false,
        loadedSrc: null,
        hasSuccessfullyLoaded: false
      });
    }, [stablePlayerName]);
    
    const handleImageError = React.useCallback(() => {
      setImageState(prev => {
        if (prev.currentSrcIndex < sources.length - 1) {
          return {
            ...prev,
            currentSrcIndex: prev.currentSrcIndex + 1,
            loadedSrc: sources[prev.currentSrcIndex + 1]
          };
        } else {
          return {
            ...prev,
            hasError: true,
            isLoading: false,
            loadedSrc: null
          };
        }
      });
    }, [sources]);
    
    const handleImageLoad = React.useCallback(() => {
      setImageState(prev => ({
        ...prev,
        isLoading: false,
        hasError: false,
        hasSuccessfullyLoaded: true
      }));
    }, []);
    
    // Set initial image source
    useEffect(() => {
      if (sources && sources[imageState.currentSrcIndex] && !imageState.loadedSrc) {
        setImageState(prev => ({
          ...prev,
          loadedSrc: sources[prev.currentSrcIndex]
        }));
      }
    }, [sources, imageState.currentSrcIndex, imageState.loadedSrc]);
    
    // Special handling for players with local images
    const isJulio = stablePlayerName === "Julio Jones" || stablePlayerName === "Jones" || stablePlayerName === "Julio";
    const isSpecialPlayer = isJulio;
    
    if (!sources || imageState.hasError) {
      // Fallback to icon if all images fail
      return (
        <div className={`${className} flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-full`} 
             style={{ width: size, height: size }}>
          <Users className="h-12 w-12 text-gray-400" />
        </div>
      );
    }
    
    return (
      <div className={`${className} relative`} style={{ width: size, height: size }}>
        {/* Loading state - only show if we haven't successfully loaded yet */}
        {imageState.isLoading && !imageState.hasSuccessfullyLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-full z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-300 border-t-gray-600"></div>
          </div>
        )}
        
        {/* Image - always rendered once we have a source */}
        {imageState.loadedSrc && (
          <img
            key={`${stablePlayerName}-${imageState.currentSrcIndex}`} // Stable key
            src={imageState.loadedSrc}
            alt={`${playerName} headshot`}
            className={`w-full h-full object-cover rounded-full shadow-xl transition-opacity duration-300 ${
              imageState.isLoading && !imageState.hasSuccessfullyLoaded ? 'opacity-0' : 'opacity-100'
            } ${
              isSpecialPlayer ? 'ring-4 ring-yellow-400 ring-opacity-75' : 'ring-2 ring-white'
            }`}
            onError={handleImageError}
            onLoad={handleImageLoad}
            loading="lazy"
            style={{ 
              // Ensure image stays visible once loaded
              visibility: imageState.hasSuccessfullyLoaded ? 'visible' : 'hidden'
            }}
          />
        )}
        
        {/* Special player indicator - only show when image is successfully loaded */}
        {isSpecialPlayer && imageState.hasSuccessfullyLoaded && !imageState.hasError && (
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg z-20">
            <span className="text-sm">👑</span>
          </div>
        )}
      </div>
    );
  });

  // Process the passed draftData (same as App.js pattern)
  useEffect(() => {
    console.log('=== DEBUGGING DATA FLOW ===');
    console.log('draftData prop received:', draftData?.length || 0, 'records');
    console.log('loading prop:', loading);
    console.log('error prop:', error);
    
    if (draftData && draftData.length > 0) {
      console.log('First 3 records:', draftData.slice(0, 3));
      
      // Check Investment field specifically
      const sampleRecord = draftData[0];
      console.log('Sample record fields:', Object.keys(sampleRecord));
      console.log('Investment field value:', sampleRecord.Investment);
      console.log('Investment field type:', typeof sampleRecord.Investment);
      
      // Check all unique Investment values
      const allInvestmentValues = [...new Set(draftData.map(r => r.Investment))];
      console.log('All unique Investment values:', allInvestmentValues);
      
      // Count by investment type
      const investmentCounts = draftData.reduce((acc, r) => {
        acc[r.Investment] = (acc[r.Investment] || 0) + 1;
        return acc;
      }, {});
      console.log('Investment counts:', investmentCounts);
    }

    if (!draftData || draftData.length === 0) return;

    console.log('=== PROCESSING PLAYER DATA ===');
    console.log('Using passed draftData:', draftData.length, 'records');
    console.log('Sample draftData record:', draftData[0]);
    
    // Check Investment field values in passed data
    const investmentValues = draftData.map(r => r.Investment);
    console.log('Investment values in passed data:', [...new Set(investmentValues)]);
    
    // Count breakdown
    const draftedCount = draftData.filter(r => r.Investment === 'Drafted').length;
    const keeperCount = draftData.filter(r => r.Investment === 'Keeper').length;
    const otherCount = draftData.length - draftedCount - keeperCount;
    
    console.log('Investment breakdown:');
    console.log('- Drafted records:', draftedCount);
    console.log('- Keeper records:', keeperCount); 
    console.log('- Other records:', otherCount);
    
    // Check if ANY player has keeper records
    const allKeeperRecords = draftData.filter(r => r.Investment === 'Keeper');
    console.log('Total keeper records in entire dataset:', allKeeperRecords.length);
    if (allKeeperRecords.length > 0) {
      console.log('Sample keeper records:', allKeeperRecords.slice(0, 3));
      console.log('Players with keeper records:', [...new Set(allKeeperRecords.map(r => r.Player))]);
    }

    // Find the highest paid player
    const investments = draftData.reduce((acc, record) => {
      const player = record.Player?.trim() || '';
      if (!acc[player]) {
        acc[player] = {
          totalCost: 0,
          draftCount: 0,
          records: []
        };
      }
      acc[player].totalCost += record.Cost || 0;
      acc[player].draftCount += 1;
      acc[player].records.push(record);
      return acc;
    }, {});

    // Get the player with highest total investment
    const topPlayer = Object.entries(investments)
      .sort(([,a], [,b]) => b.totalCost - a.totalCost)[0];

    if (!topPlayer) return;

    const [playerName, stats] = topPlayer;
    
    console.log('=== FEATURED PLAYER ===');
    console.log('Featured player:', playerName, 'Total cost:', stats.totalCost);
    console.log('Featured player records:', stats.records.length);
    console.log('Featured player sample record:', stats.records[0]);
    console.log('Featured player Investment values:', stats.records.map(r => r.Investment));

    // Calculate yearly investments for this player
    const yearlyData = stats.records.reduce((acc, record) => {
      const year = record.Year;
      const existing = acc.find(item => item.year === year);
      if (existing) {
        existing.amount += record.Cost || 0;
      } else {
        acc.push({
          year: year,
          amount: record.Cost || 0,
          investment: record.Investment || 'Drafted'
        });
      }
      return acc;
    }, []).sort((a, b) => a.year - b.year);

    // Calculate manager investments for this player - FIXED: Using capitalized keys like InvestmentChart
    const managerData = stats.records.reduce((acc, record) => {
      const manager = record.Manager;
      const investment = record.Investment; // Use the standardized Investment field
      
      console.log('Processing manager record:', {
        manager: manager,
        investment: investment,
        cost: record.Cost
      });
      
      const existing = acc.find(item => item.manager === manager);
      if (existing) {
        // FIXED: Use capitalized keys to match InvestmentChart pattern
        existing.Drafted += investment === 'Drafted' ? (record.Cost || 0) : 0;
        existing.Keeper += investment === 'Keeper' ? (record.Cost || 0) : 0;
        existing.total += record.Cost || 0;
      } else {
        acc.push({
          manager: manager,
          Drafted: investment === 'Drafted' ? (record.Cost || 0) : 0, // Capitalized to match InvestmentChart
          Keeper: investment === 'Keeper' ? (record.Cost || 0) : 0,   // Capitalized to match InvestmentChart
          total: record.Cost || 0
        });
      }
      return acc;
    }, []).sort((a, b) => b.total - a.total);

    console.log('=== MANAGER DATA FINAL ===');
    console.log('Final manager data:', managerData);
    managerData.forEach(m => {
      console.log(`${m.manager}: Drafted=${m.Drafted}, Keeper=${m.Keeper}, Total=${m.total}`);
    });

    // Get the featured player's year range for chart domain
    const featuredPlayerYears = stats.records.map(r => r.Year);
    const playerMinYear = Math.min(...featuredPlayerYears);
    const playerMaxYear = Math.max(...featuredPlayerYears);
    
    // Filter all data to only show dots within the featured player's year range
    const filteredData = draftData.filter(record => 
      record.Year >= playerMinYear && record.Year <= playerMaxYear
    );
    
    // Create all dots for the chart
    const allDots = filteredData.map((record, index) => {
      const jitterRange = 0.3;
      const jitter = (Math.random() - 0.5) * jitterRange * 2;
      const isHighlighted = (record.Player?.trim() || '') === playerName;
      
      return {
        year: record.Year + (isHighlighted ? 0 : jitter),
        amount: record.Cost || 0,
        player: record.Player?.trim() || '',
        manager: record.Manager,
        investment: record.Investment, // Use standardized Investment field
        isHighlighted: isHighlighted,
        id: `${record.Player?.trim()}-${record.Year}-${record.Manager}-${index}`
      };
    });

    console.log('Total dots created:', allDots.length);

    setPlayerData({
      name: playerName,
      totalCost: stats.totalCost,
      draftCount: stats.draftCount,
      years: [...new Set(stats.records.map(r => r.Year))].length,
      managers: [...new Set(stats.records.map(r => r.Manager))].length,
      positions: [...new Set(stats.records.map(r => r.Position))],
      yearRange: { min: playerMinYear, max: playerMaxYear }
    });
    setYearlyInvestments(yearlyData);
    setManagerInvestments(managerData);
    setAllPlayerDots(allDots);

    // Trigger animation
    setTimeout(() => setIsAnimated(true), 100);
  }, [draftData]); // Use draftData directly as dependency

  // Chart dimensions callback
  const handleChartResize = (width, height) => {
    setChartDimensions({ width, height });
  };

  // Custom tooltip component - Mobile optimized
  const CustomTooltipContent = () => {
    if (!showTooltip || !hoveredDot) return null;

    const isHighlighted = hoveredDot.isHighlighted;
    const hoveredYear = Math.round(hoveredDot.year || 0);
    const value = hoveredDot.amount || 0;
    
    // Find featured player's investment in this specific year for comparison
    const featuredThisYear = allPlayerDots.find(dot => 
      dot.isHighlighted && Math.round(dot.year) === hoveredYear
    );
    
    const featuredAmount = featuredThisYear?.amount || 0;

    // Mobile-responsive tooltip positioning and sizing
    const isMobile = window.innerWidth < 640;
    const tooltipWidth = isMobile ? '240px' : '220px';
    
    const tooltipStyle = {
      position: 'fixed',
      left: Math.min(mousePosition.x, window.innerWidth - (isMobile ? 250 : 230)),
      top: Math.max(10, mousePosition.y - (isMobile ? 150 : 100)),
      backgroundColor: 'rgba(15, 23, 42, 0.98)',
      border: '1px solid rgba(71, 85, 105, 0.3)',
      borderRadius: isMobile ? '16px' : '12px',
      boxShadow: '0 15px 30px -8px rgba(0, 0, 0, 0.6)',
      padding: isMobile ? '16px' : '12px',
      width: tooltipWidth,
      maxWidth: tooltipWidth,
      color: '#f8fafc',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      fontSize: isMobile ? '13px' : '12px',
      zIndex: 1000,
      pointerEvents: 'none'
    };

    return (
      <div style={tooltipStyle}>
        {/* Header */}
        <div style={{
          color: '#fbbf24',
          fontWeight: '600',
          fontSize: '11px',
          marginBottom: '8px',
          textAlign: 'center',
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}>
          {hoveredYear} Season
        </div>

        {isHighlighted ? (
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(14, 165, 233, 0.1))',
            borderRadius: '12px',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            padding: '12px',
            position: 'relative'
          }}>
            {/* Crown Badge */}
            <div style={{
              position: 'absolute',
              top: '-4px',
              right: '8px',
              backgroundColor: '#fbbf24',
              color: '#0f172a',
              padding: '2px 6px',
              borderRadius: '6px',
              fontSize: '8px',
              fontWeight: '700',
              textTransform: 'uppercase'
            }}>
              👑
            </div>
            
            {/* Investment Amount */}
            <div style={{ 
              textAlign: 'center',
              marginBottom: '8px',
              marginTop: '4px'
            }}>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: '800',
                background: 'linear-gradient(135deg, #06b6d4, #0ea5e9)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px'
              }}>
                ${value}
              </div>
              <div style={{ 
                fontSize: '8px', 
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Investment
              </div>
            </div>
            
            {/* Player Name */}
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '700',
              color: '#f1f5f9',
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              {hoveredDot.player}
            </div>
            
            {/* Details */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '10px'
            }}>
              <span style={{ color: '#94a3b8' }}>Manager:</span>
              <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{hoveredDot.manager}</span>
            </div>
            
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '10px'
            }}>
              <span style={{ color: '#94a3b8' }}>Type:</span>
              <span style={{
                backgroundColor: hoveredDot.investment === 'Drafted' ? '#3b82f6' : '#f59e0b',
                color: 'white',
                padding: '1px 4px',
                borderRadius: '4px',
                fontSize: '8px',
                fontWeight: '600'
              }}>
                {hoveredDot.investment}
              </span>
            </div>
          </div>
        ) : (
          <div style={{ padding: '12px' }}>
            {/* Investment Amount */}
            <div style={{ 
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '800',
                color: '#06b6d4',
                letterSpacing: '-0.5px'
              }}>
                ${value}
              </div>
              <div style={{ 
                fontSize: '8px', 
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Investment
              </div>
            </div>
            
            {/* Player Name */}
            <div style={{ 
              fontSize: '13px', 
              fontWeight: '700',
              color: '#f1f5f9',
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              {hoveredDot.player}
            </div>
            
            {/* Details */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '6px',
              fontSize: '9px'
            }}>
              <span style={{ color: '#94a3b8' }}>Manager:</span>
              <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{hoveredDot.manager}</span>
            </div>
            
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '9px'
            }}>
              <span style={{ color: '#94a3b8' }}>Type:</span>
              <span style={{
                backgroundColor: hoveredDot.investment === 'Drafted' ? '#1e40af' : '#92400e',
                color: 'white',
                padding: '1px 4px',
                borderRadius: '3px',
                fontSize: '8px',
                fontWeight: '600'
              }}>
                {hoveredDot.investment}
              </span>
            </div>
            
            {/* Comparison Section */}
            <div style={{
              backgroundColor: 'rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '6px',
              padding: '6px',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '8px', 
                color: '#06b6d4',
                fontWeight: '600',
                lineHeight: '1.2'
              }}>
                📊 {playerData.name} {hoveredYear}: ${featuredAmount}
                <br />
                Career Total: ${playerData.totalCost}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-3xl p-8 mb-8">
        <div className="animate-pulse">
          <div className="h-8 bg-purple-200 rounded-lg mb-4 w-1/3"></div>
          <div className="h-4 bg-purple-200 rounded-lg mb-8 w-2/3"></div>
          <div className="h-32 bg-purple-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !playerData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-3xl p-8 mb-8">
        <h3 className="text-lg font-medium text-red-800 mb-2">Unable to Load Player Data</h3>
        <p className="text-red-600 mb-4">{error || 'No player data available'}</p>
        <div className="text-sm text-red-600">
          <p><strong>Debug Info:</strong></p>
          <p>Draft Data Length: {draftData?.length || 0}</p>
          <p>Player Data: {playerData ? 'Available' : 'Not Available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 rounded-3xl shadow-2xl border border-white/50 p-4 sm:p-6 lg:p-8 mb-8 overflow-hidden">
      {/* Featured Player Memorial */}
      <div className="bg-gradient-to-r from-cyan-400 via-teal-400 to-blue-500 rounded-2xl p-4 sm:p-6 lg:p-8 text-white shadow-2xl mb-6 lg:mb-8">
        
        {/* Mobile Layout */}
        <div className="block lg:hidden">
          {/* Mobile Header with Player Info */}
          <div className="flex flex-col items-center text-center space-y-4 mb-6">
            <div className={`transition-all duration-1000 ${isAnimated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
              <PlayerHeadshot 
                playerName={playerData.name}
                size={100}
                className="group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            
            <div className={`transition-all duration-1000 delay-300 ${isAnimated ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
                {playerData.name}
              </h3>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/30 inline-block mb-3">
                <span className="text-lg font-bold text-yellow-300">${playerData.totalCost}</span>
                <span className="text-sm text-cyan-100 ml-1">Total Investment</span>
              </div>
            </div>
          </div>

          {/* Mobile Description */}
          <div className={`text-center mb-6 transition-all duration-1000 delay-500 ${isAnimated ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            <p className="text-sm sm:text-base text-cyan-100 leading-relaxed">
              is the highest-paid player by Beasts of Bellevue managers, earning{' '}
              <span className="font-bold text-yellow-300">${playerData.totalCost}</span> over{' '}
              <span className="font-bold">{playerData.draftCount}</span> drafts.
            </p>
          </div>

          {/* Mobile Stats Grid - Compact 2x2 */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`bg-white/20 backdrop-blur-sm rounded-lg p-3 text-center transition-all duration-1000 delay-600 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Target className="h-6 w-6 mx-auto mb-1 text-yellow-300" />
              <div className="text-lg font-bold text-white">{playerData.draftCount}</div>
              <div className="text-xs text-cyan-100">Drafts</div>
            </div>
            <div className={`bg-white/20 backdrop-blur-sm rounded-lg p-3 text-center transition-all duration-1000 delay-700 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Users className="h-6 w-6 mx-auto mb-1 text-yellow-300" />
              <div className="text-lg font-bold text-white">{playerData.managers}</div>
              <div className="text-xs text-cyan-100">Managers</div>
            </div>
            <div className={`bg-white/20 backdrop-blur-sm rounded-lg p-3 text-center transition-all duration-1000 delay-800 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Calendar className="h-6 w-6 mx-auto mb-1 text-yellow-300" />
              <div className="text-lg font-bold text-white">{playerData.years}</div>
              <div className="text-xs text-cyan-100">Years</div>
            </div>
            <div className={`bg-white/20 backdrop-blur-sm rounded-lg p-3 text-center transition-all duration-1000 delay-900 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Award className="h-6 w-6 mx-auto mb-1 text-yellow-300" />
              <div className="text-lg font-bold text-white">{playerData.positions.join(', ')}</div>
              <div className="text-xs text-cyan-100">Positions</div>
            </div>
          </div>
        </div>

        {/* Desktop Layout - Hidden on mobile, unchanged */}
        <div className="hidden lg:flex flex-col lg:flex-row items-center justify-between">
          
          {/* Player Headshot Section */}
          <div className="flex items-center space-x-8 mb-6 lg:mb-0">
            <div className={`transition-all duration-1000 ${isAnimated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
              <PlayerHeadshot 
                playerName={playerData.name}
                size={120}
                className="group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            
            {/* Player Text */}
            <div className="text-center lg:text-left">
              <div className={`transition-all duration-1000 delay-300 ${isAnimated ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                <h3 className="text-4xl lg:text-6xl font-bold mb-3 text-white">
                  {playerData.name}
                </h3>
                <p className="text-lg lg:text-xl text-cyan-100 leading-relaxed max-w-lg">
                  is the player who has been paid the most by Beasts of Bellevue managers. 
                  <span className="font-semibold"> {playerData.name}</span> has been paid a total of{' '}
                  <span className="font-bold text-yellow-300">${playerData.totalCost}</span> dollars 
                  over the course of <span className="font-bold">{playerData.draftCount}</span> fantasy drafts.
                </p>
              </div>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center transition-all duration-1000 delay-500 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <DollarSign className="h-8 w-8 mx-auto mb-2 text-yellow-300" />
              <div className="text-2xl font-bold text-white">${playerData.totalCost}</div>
              <div className="text-sm text-cyan-100">Total Investment</div>
            </div>
            <div className={`bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center transition-all duration-1000 delay-600 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Target className="h-8 w-8 mx-auto mb-2 text-yellow-300" />
              <div className="text-2xl font-bold text-white">{playerData.draftCount}</div>
              <div className="text-sm text-cyan-100">Times Drafted</div>
            </div>
            <div className={`bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center transition-all duration-1000 delay-700 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Users className="h-8 w-8 mx-auto mb-2 text-yellow-300" />
              <div className="text-2xl font-bold text-white">{playerData.managers}</div>
              <div className="text-sm text-cyan-100">Different Managers</div>
            </div>
            <div className={`bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center transition-all duration-1000 delay-800 ${isAnimated ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
              <Calendar className="h-8 w-8 mx-auto mb-2 text-yellow-300" />
              <div className="text-2xl font-bold text-white">{playerData.years}</div>
              <div className="text-sm text-cyan-100">Different Years</div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-1 xl:grid-cols-2 lg:gap-8">
        {/* Yearly Investment History - Custom SVG Chart */}
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm">
          <div className="mb-3">
            <h4 className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 mr-2" />
              <span className="leading-tight">History of {playerData.name}'s Beasts of Bellevue Era</span>
            </h4>
          </div>
          <div className="h-48 sm:h-56 lg:h-64 relative bg-white">
            {/* Chart Container - More padding to prevent cutoff, responsive */}
            <div className="absolute" style={{ top: 12, bottom: 20, left: 24, right: 12 }}>
              {/* Y-axis labels - Smaller on mobile */}
              <div className="absolute -left-6 inset-y-0 flex flex-col justify-between text-xs text-gray-500">
                <span>80</span>
                <span>60</span>
                <span>40</span>
                <span>20</span>
                <span>0</span>
              </div>
              
              {/* X-axis labels - Hide some years on mobile if needed */}
              <div className="absolute -bottom-5 inset-x-0 flex justify-between text-xs text-gray-500">
                {Array.from(
                  { length: playerData.yearRange.max - playerData.yearRange.min + 1 }, 
                  (_, i) => {
                    const year = playerData.yearRange.min + i;
                    // On mobile, show every other year if range is large
                    const yearSpan = playerData.yearRange.max - playerData.yearRange.min;
                    const showYear = yearSpan <= 6 || i % 2 === 0 || window.innerWidth >= 640;
                    return (
                      <span key={i} className={showYear ? '' : 'hidden sm:inline'}>
                        {year}
                      </span>
                    );
                  }
                )}
              </div>
              
              {/* Grid lines */}
              <svg className="absolute inset-0 w-full h-full">
                {/* Horizontal grid lines */}
                {[0, 20, 40, 60, 80].map(value => (
                  <line
                    key={value}
                    x1="0"
                    y1={`${100 - (value / 80) * 100}%`}
                    x2="100%"
                    y2={`${100 - (value / 80) * 100}%`}
                    stroke="#f3f4f6"
                    strokeDasharray="3,3"
                  />
                ))}
                
                {/* Vertical grid lines */}
                {Array.from(
                  { length: playerData.yearRange.max - playerData.yearRange.min + 1 }, 
                  (_, i) => (
                    <line
                      key={i}
                      x1={`${(i / (playerData.yearRange.max - playerData.yearRange.min)) * 100}%`}
                      y1="0"
                      x2={`${(i / (playerData.yearRange.max - playerData.yearRange.min)) * 100}%`}
                      y2="100%"
                      stroke="#f3f4f6"
                      strokeDasharray="3,3"
                    />
                  )
                )}
                
                {/* Background dots (grey/purple when hovered) */}
                {allPlayerDots.filter(dot => !dot.isHighlighted).map((dot, index) => {
                  const yearSpan = playerData.yearRange.max - playerData.yearRange.min;
                  const xPercent = yearSpan > 0 ? ((dot.year - playerData.yearRange.min) / yearSpan) * 100 : 50;
                  const yPercent = 100 - ((dot.amount / 80) * 100);
                  
                  const handleMouseEnter = (e) => {
                    console.log('Chart dot hovered:', dot.player, dot.amount);
                    setHoveredDot(dot);
                    setMousePosition({
                      x: e.clientX + 15,
                      y: e.clientY - 10
                    });
                    setShowTooltip(true);
                  };

                  const handleMouseLeave = () => {
                    setShowTooltip(false);
                    setHoveredDot(null);
                  };

                  const isCurrentlyHovered = hoveredDot?.id === dot.id;

                  return (
                    <circle
                      key={dot.id || `bg-${index}`}
                      cx={`${xPercent}%`}
                      cy={`${yPercent}%`}
                      r={isCurrentlyHovered ? (window.innerWidth < 640 ? 3 : 4) : (window.innerWidth < 640 ? 2 : 3)}
                      fill={isCurrentlyHovered ? "#8b5cf6" : "#e2e8f0"}
                      stroke={isCurrentlyHovered ? "#7c3aed" : "#cbd5e1"}
                      strokeWidth={isCurrentlyHovered ? 1.5 : 0.5}
                      fillOpacity={isCurrentlyHovered ? 0.9 : 0.8}
                      style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
                
                {/* Foreground dots (cyan - highlighted player) */}
                {allPlayerDots.filter(dot => dot.isHighlighted).map((dot, index) => {
                  const yearSpan = playerData.yearRange.max - playerData.yearRange.min;
                  const xPercent = yearSpan > 0 ? ((dot.year - playerData.yearRange.min) / yearSpan) * 100 : 50;
                  const yPercent = 100 - ((dot.amount / 80) * 100);
                  
                  const handleMouseEnter = (e) => {
                    console.log('Chart dot hovered:', dot.player, dot.amount);
                    setHoveredDot(dot);
                    setMousePosition({
                      x: e.clientX + 15,
                      y: e.clientY - 10
                    });
                    setShowTooltip(true);
                  };

                  const handleMouseLeave = () => {
                    setShowTooltip(false);
                    setHoveredDot(null);
                  };

                  return (
                    <circle
                      key={dot.id || `fg-${index}`}
                      cx={`${xPercent}%`}
                      cy={`${yPercent}%`}
                      r={window.innerWidth < 640 ? 4 : 6}
                      fill="#06b6d4"
                      stroke="#0891b2"
                      strokeWidth={window.innerWidth < 640 ? 1.5 : 2}
                      fillOpacity={1}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </svg>
            </div>
          </div>
          
          {/* Legend - Responsive */}
          <div className="flex justify-center mt-2 sm:mt-3 space-x-4 sm:space-x-6">
            <div className="flex items-center">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-gray-300 rounded-full mr-1 sm:mr-2 opacity-80"></div>
              <span className="text-xs text-gray-600">All Players</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-cyan-500 rounded-full mr-1 sm:mr-2"></div>
              <span className="text-xs text-gray-600">{playerData.name}</span>
            </div>
          </div>
        </div>

        {/* Manager Investment Breakdown */}
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm">
          <div className="mb-3">
            <h4 className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 mr-2" />
              <span className="leading-tight">Which managers invested in {playerData.name}?</span>
            </h4>
          </div>
          <div className="h-48 sm:h-56 lg:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={managerInvestments} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis 
                  dataKey="manager" 
                  stroke="#6b7280"
                  fontSize={9}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  angle={window.innerWidth < 640 ? -45 : 0}
                  textAnchor={window.innerWidth < 640 ? "end" : "middle"}
                  height={window.innerWidth < 640 ? 60 : 30}
                />
                <YAxis 
                  stroke="#6b7280"
                  fontSize={9}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const total = payload.reduce((sum, entry) => sum + entry.value, 0);
                      
                      // Find the manager's records for this player to get years
                      const managerRecords = managerInvestments.find(m => m.manager === label);
                      const playerRecords = playerData ? draftData.filter(record => 
                        record.Manager === label && 
                        record.Player?.trim() === playerData.name?.trim()
                      ) : [];
                      
                      const years = [...new Set(playerRecords.map(r => r.Year))].sort();
                      const yearRangeText = years.length > 0 ? 
                        (years.length === 1 ? years[0].toString() : 
                         years.length === 2 ? `${years[0]} & ${years[1]}` :
                         `${years[0]}-${years[years.length - 1]}`) : 'No years';
                      
                      return (
                        <div style={{
                          backgroundColor: 'rgba(15, 23, 42, 0.98)',
                          border: '1px solid rgba(71, 85, 105, 0.4)',
                          borderRadius: '16px',
                          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '500',
                          padding: '16px',
                          minWidth: '220px',
                          backdropFilter: 'blur(20px)',
                          WebkitBackdropFilter: 'blur(20px)',
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}>
                          {/* Header */}
                          <div style={{
                            color: '#fbbf24',
                            fontWeight: '700',
                            fontSize: '12px',
                            marginBottom: '12px',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            borderBottom: '1px solid rgba(251, 191, 36, 0.3)',
                            paddingBottom: '8px'
                          }}>
                            {label}'s Investment in {playerData?.name}
                          </div>

                          {/* Years Section */}
                          <div style={{
                            marginBottom: '12px',
                            padding: '8px 12px',
                            backgroundColor: 'rgba(6, 182, 212, 0.15)',
                            border: '1px solid rgba(6, 182, 212, 0.3)',
                            borderRadius: '8px',
                            textAlign: 'center'
                          }}>
                            <div style={{
                              color: '#06b6d4',
                              fontSize: '10px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '4px'
                            }}>
                              Draft Years
                            </div>
                            <div style={{
                              color: '#e2e8f0',
                              fontSize: '14px',
                              fontWeight: '700'
                            }}>
                              {yearRangeText}
                            </div>
                            {years.length > 2 && (
                              <div style={{
                                color: '#94a3b8',
                                fontSize: '9px',
                                marginTop: '2px'
                              }}>
                                ({years.join(', ')})
                              </div>
                            )}
                          </div>

                          {/* Investment Breakdown */}
                          <div style={{ marginBottom: '12px' }}>
                            {payload.map((entry, index) => {
                              const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                              const isDrafted = entry.dataKey === 'Drafted';
                              
                              return (
                                <div key={index} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: '8px',
                                  padding: '8px 12px',
                                  backgroundColor: isDrafted ? 'rgba(139, 92, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                  borderRadius: '8px',
                                  border: `1px solid ${isDrafted ? 'rgba(139, 92, 246, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: entry.color,
                                      marginRight: '8px',
                                      boxShadow: `0 0 8px ${entry.color}40`
                                    }}></div>
                                    <span style={{
                                      color: '#f1f5f9',
                                      fontSize: '12px',
                                      fontWeight: '600'
                                    }}>
                                      {entry.dataKey}
                                    </span>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                      color: isDrafted ? '#a78bfa' : '#fbbf24',
                                      fontWeight: '700',
                                      fontSize: '14px'
                                    }}>
                                      ${entry.value}
                                    </div>
                                    <div style={{
                                      color: '#94a3b8',
                                      fontSize: '10px',
                                      fontWeight: '500'
                                    }}>
                                      {percentage}%
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Total Section */}
                          <div style={{
                            borderTop: '1px solid rgba(71, 85, 105, 0.4)',
                            paddingTop: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{
                              color: '#e2e8f0',
                              fontSize: '12px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Total Investment
                            </span>
                            <span style={{
                              color: '#06b6d4',
                              fontSize: '16px',
                              fontWeight: '800',
                              textShadow: '0 0 10px rgba(6, 182, 212, 0.5)'
                            }}>
                              ${total}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {/* FIXED: Use capitalized dataKey values to match data structure */}
                <Bar dataKey="Drafted" stackId="a" fill="#8b5cf6" name="Drafted" />
                <Bar dataKey="Keeper" stackId="a" fill="#f59e0b" name="Keeper" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend - Responsive */}
          <div className="flex justify-center mt-2 sm:mt-3 space-x-4 sm:space-x-6">
            <div className="flex items-center">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-500 rounded mr-1 sm:mr-2"></div>
              <span className="text-xs text-gray-600">Drafted</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-amber-500 rounded mr-1 sm:mr-2"></div>
              <span className="text-xs text-gray-600">Keeper</span>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Tooltip Overlay */}
      <CustomTooltipContent />
    </div>
  );
};

export default HighestPaidPlayerMemorial;